'use client';

import { useEffect } from 'react';

/**
 * Captures affiliate conversion tokens from the landing URL and stores them in
 * 30-day cookies so the server-side postback utils can read them at conversion
 * time:
 *   - TrafficStars: `?clickid=XXX`  -> `ts_clickid`
 *   - ExoClick / MagSrv: `?exoclick=XXX` -> `ec_tag`
 *
 * This is a client-side fallback; the proxy captures the same params server-side
 * before any JS runs. Mounted in the root layout — runs once per page load.
 */

// Permissive: real affiliate click IDs / tags vary (hex, base64url, UUIDs,
// dotted). Only reject cookie/URL-breaking characters so a valid value is
// never dropped.
const TOKEN_PATTERN = /^[A-Za-z0-9._~%:+/=-]{1,512}$/;
const CLICKID_COOKIE = 'ts_clickid';
const EXOCLICK_COOKIE = 'ec_tag';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days

export function ClickIdCapture() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // SameSite=Lax so the cookie travels on top-level navigations (including
    // the OAuth redirect round-trip) but not on cross-site subrequests.
    const setCookie = (name: string, value: string) => {
      if (!TOKEN_PATTERN.test(value)) return;
      document.cookie = `${name}=${value}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
    };

    const clickid = params.get('clickid');
    if (clickid) setCookie(CLICKID_COOKIE, clickid);

    const exoclick = params.get('exoclick');
    if (exoclick) setCookie(EXOCLICK_COOKIE, exoclick);
  }, []);

  return null;
}
